import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ThemeProvider, Box, Typography, TableContainer, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import { theme } from '../../Medical_History_Forms/themeprovider';
import Grid from '@mui/material/Grid2';
import { convertUnderscoreToTitle, infoTextStyle, sectionTitleStyle } from '../../../utils/commonUtils';
import { useLoader } from '../../loaderProvider/loader';
import styles from '../oocyteRetrieval/oocyteRetrival.module.css';
import { getSurgicalSpermRetrieval, SurgicalSpermRetrievalType } from './spermRetrieval.helper';
import { DocumentChips, FileViewerModal, UploadedImageType } from '../../common/ImageUploadModal';

interface LaparoscopyViewDetailsProps {
  record: number; 
  onBack: () => void;
  onEdit?: () => void; 
  canEditOrAdd?: boolean;
  canView?: boolean;
}

const SurgicalSpermRetrievalDetails: React.FC<LaparoscopyViewDetailsProps> = ({ record, onBack, onEdit, canEditOrAdd, canView }) => {
  const { t: tGeneral } = useTranslation('general');
  const { t: tSurgery } = useTranslation('surgery_config');
  const { showLoader, hideLoader } = useLoader();
  const [retrievalData, setRetrievalData] = useState<SurgicalSpermRetrievalType | null>(null);

  const [openViewer, setOpenViewer] = useState(false);
  const [viewerInitialIndex, setViewerInitialIndex] = useState(0);

  // Convert Files[] to UploadedImageType[] for DocumentChips
  const uploadedFiles: UploadedImageType[] = (retrievalData?.images || []).map((img, index) => {
    // Use file_name from API if available, otherwise extract from URL
    let fileName = img.file_name || `Document_${index + 1}`;
    if (!img.file_name && img.image) {
      try {
        const urlPath = new URL(img.image).pathname;
        const extractedName = urlPath.split('/').pop();
        if (extractedName && extractedName.length > 0) {
          fileName = decodeURIComponent(extractedName);
        }
      } catch {
        // Keep default name if URL parsing fails
      }
    }
    return {
      name: fileName,
      image: img.image || '',
      id: img.id,
      size: img.file_size ? (img.file_size / (1024 * 1024)).toFixed(2) : undefined,
      sizeBytes: img.file_size,
      file_extension: img.file_extension,
      isExisting: true,
    };
  });

  const handleViewFile = (index: number) => {
    setViewerInitialIndex(index);
    setOpenViewer(true);
  };

  useEffect(() => {
    if (!record) return;

    showLoader();
    getSurgicalSpermRetrieval(record)
      .then((data) => setRetrievalData(data))
      .catch(() => {
        // eslint-disable-next-line no-console
        console.error('Error fetching Surgical Sperm Retrieval data');
        setRetrievalData(null); // Set to default on error
      })
      .finally(() => hideLoader());
  }, [record, showLoader, hideLoader]);

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ p: 3 }}>
        <Grid container spacing={4} mb={3}>
          <Box sx={{ width: '100%'}}>
            <Typography variant="h6" sx={{ ...infoTextStyle }}>
              {tGeneral('details')}
            </Typography>
          </Box>
          {[ 
            { label: tGeneral('date'), value: retrievalData?.date },
            { label: tGeneral('startTime'), value: retrievalData?.time },
            { label: tSurgery('indication'), value: convertUnderscoreToTitle(retrievalData?.indication) },
            { label: tSurgery('clinician'), value: retrievalData?.clinician_name },
            { label: tSurgery('specimenType'), value: convertUnderscoreToTitle(retrievalData?.specimen_type) },
            { label: tSurgery('methodOfSurgicalSpermRetrieval'), value: retrievalData?.method_of_surgical_sperm_retrieval },
            { label: tSurgery('surgeonName'), value: convertUnderscoreToTitle(retrievalData?.surgeon_name) },
            { label: tSurgery('anesthetist'), value: retrievalData?.anesthetist_name },
            { label: tSurgery('anesthesia'), value: convertUnderscoreToTitle(retrievalData?.anesthesia) },
            { label: tSurgery('andrologist'), value: retrievalData?.andrologist_name },
            { label: tSurgery('witnessAndrologist'), value: retrievalData?.witness_andrologist_name },
            { label: tSurgery('site'), value: convertUnderscoreToTitle(retrievalData?.site) },
          ].map((field, index) => (
            <Grid key={index} size={3} sx={{ textAlign: 'left' }}>
              <Typography variant="body1" sx={sectionTitleStyle}>{field.label}</Typography>
              <Typography variant="body2" sx={infoTextStyle}>{field.value || '-'}</Typography>
            </Grid>
          ))}
        </Grid>


        <TableContainer>
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  height: '2rem',
                  background: 'rgba(250, 250, 250, 1)',
                  border: 'none',
                  '& .MuiTableCell-root': {
                    padding: '0.6rem 0.6rem',
                  },
                }}
              >
                {[tSurgery('site'), tSurgery('spermCount'), tSurgery('motility'), tGeneral('remark')].map((title, index) => (
                  <TableCell
                    key={index}
                    sx={{
                      fontFamily: 'Nunito, sans-serif',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      lineHeight: '1.45',
                      textAlign: 'left',
                      color: 'rgba(35, 35, 35, 1)',
                    }}
                  >
                    {title}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow key={1}>
                <TableCell>{tSurgery('right')}</TableCell>
                <TableCell>{retrievalData?.right_sperm_count || '-'}</TableCell>
                <TableCell>{retrievalData?.right_motility || '-'}</TableCell>
                <TableCell>{retrievalData?.right_remark || '-'}</TableCell>
              </TableRow>
              <TableRow key={2}>
                <TableCell>{tSurgery('left')}</TableCell>
                <TableCell>{retrievalData?.left_sperm_count || '-'}</TableCell>
                <TableCell>{retrievalData?.left_motility || '-'}</TableCell>
                <TableCell>{retrievalData?.left_remark || '-'}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>


        <Grid container spacing={4} mb={3} mt={2}>
          <Grid size={3} sx={{ textAlign: 'left' }}>
            <Typography variant="body1" sx={sectionTitleStyle}>{tSurgery('complications')}</Typography>
            <Typography variant="body2" sx={infoTextStyle}>
              {retrievalData?.complications  || '-'} 
            </Typography>
          </Grid>
          <Grid size={3} sx={{ textAlign: 'left' }}>
            <Typography variant="body1" sx={sectionTitleStyle}>{tGeneral('remark')}</Typography>
            <Typography variant="body2" sx={infoTextStyle}>
              {retrievalData?.remarks  || '-'} 
            </Typography>
          </Grid>
        </Grid>


        {/* Document Chips for uploaded files */}
        {uploadedFiles.length > 0 && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="body1" sx={{ ...sectionTitleStyle, mb: 1 }}>
              Documents
            </Typography>
            <DocumentChips
              files={uploadedFiles}
              onViewClick={handleViewFile}
            />
          </Box>
        )}

        {/* File Viewer Modal */}
        <FileViewerModal
          open={openViewer}
          onClose={() => setOpenViewer(false)}
          files={uploadedFiles}
          initialIndex={viewerInitialIndex}
          title={tGeneral('preview')}
        />
        
        <Box className={styles.customButtonDiv}>
          <button
            type="button"
            className={styles.resetButton}
            onClick={onBack}
          >
            {tGeneral('backToList')}
          </button>
          {canEditOrAdd && canView && (
            <button
              type="button"
              className={styles.customButton}
              onClick={onEdit}
            >
              <span style={{ fontWeight: 'bold' }}>
                {tGeneral('edit')}
              </span>
            </button>
          )}

        </Box>

      </Box>
    </ThemeProvider>
  );
};

export default SurgicalSpermRetrievalDetails;
